import type { EnrichmentAsk, EnrichmentResult, Item } from "@larder/shared";
import { httpsCallable } from "firebase/functions";
import { functions } from "./extract";
import { applyEnrichment } from "./items";

/**
 * Client side of enrichment.
 *
 * Fired AFTER a save, never before it. The item is on the list and usable the
 * instant it is typed; this fills in the category, the pack size, the macros and
 * the glyph a moment later. Nothing the user is waiting on depends on it, which
 * is why every failure path here ends in a shrug rather than a message.
 */

const call = httpsCallable<
  { items: EnrichmentAsk[]; timezone: string },
  EnrichmentResult
>(functions, "enrichItems", { timeout: 60_000 });

/**
 * Enriches items that have not been enriched yet, and writes the answers back.
 *
 * Takes the whole basket at once because the server batches: thirty items are
 * one call and roughly one call's latency. Enriching them one at a time would
 * be thirty calls, thirty times the cost, and slower in wall-clock terms.
 *
 * Returns quietly on ANY failure. The caller is a fire-and-forget effect and
 * there is nothing useful it could do with an error — the items are saved, and
 * an un-enriched item is a state the UI already renders correctly.
 */
export async function enrichItems(
  locId: string,
  items: Item[],
  timezone: string,
): Promise<void> {
  const todo = items.filter((i) => i.enrichedAt === null && i.name.trim());
  if (todo.length === 0) return;

  try {
    const res = await call({
      items: todo.map((i) => ({
        name: i.name,
        brand: i.brand,
        sizeText: i.sizeText,
      })),
      timezone,
    });

    const answers = res.data?.items ?? [];
    // Write-backs are independent, so one failing document must not abandon the
    // rest. allSettled rather than all, for exactly that reason.
    await Promise.allSettled(
      todo.map((item, i) => {
        const found = answers[i];
        return found ? applyEnrichment(locId, item, found) : Promise.resolve();
      }),
    );
  } catch {
    // Deliberately silent. See the note above the function.
  }
}
